import { RideItemType, SegmentType } from '@component/Ride/types';
import {
  addOneDay,
  addOrSubtractDuration,
  addSeconds,
  formatTime,
  sumTimes,
  whichContractor,
} from '@src/utils';
import { toast } from 'react-toastify';
import { addDays, format } from 'date-fns';
import tzlookup from 'tz-lookup';
import moment from 'moment-timezone';
import { locationType } from '@src/types';
import store from '@store/store';
import {
  setCreateSOByRide,
  setIsRideCopy,
  setIsRideSaved,
  setIsSOSaved,
} from '@store/slices/rides/ridesSlice';
import { AlertModal } from '@src/provider/AlertProvider';
import { allStatuses, RIDE_STATUS_IDS } from '@utils/helpers';
import { logger } from '@utils/logger';
import { toastMessage } from '@utils/toastMessage';

export type StepStatus = 'success' | 'error' | 'pending';
export type RideMemberViewMode = 'COMPACT' | 'DETAILED';

export const RIDE_MEMBER_VIEW_MODE_STORAGE_KEY = 'rideMemberViewMode';
export const RIDE_MEMBER_VIEW_MODE_CHANGE_EVENT =
  'ride-member-view-mode-change';

export const isRideMemberViewMode = (
  value: string
): value is RideMemberViewMode => value === 'COMPACT' || value === 'DETAILED';

export const getRideMemberViewMode = (): RideMemberViewMode => {
  if (typeof window === 'undefined') return 'DETAILED';

  const storedMode = window.localStorage.getItem(
    RIDE_MEMBER_VIEW_MODE_STORAGE_KEY
  );

  return storedMode && isRideMemberViewMode(storedMode)
    ? storedMode
    : 'DETAILED';
};

export const setRideMemberViewMode = (mode: RideMemberViewMode) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(RIDE_MEMBER_VIEW_MODE_STORAGE_KEY, mode);
  window.dispatchEvent(
    new CustomEvent(RIDE_MEMBER_VIEW_MODE_CHANGE_EVENT, {
      detail: { mode },
    })
  );
};

export const isCompactRideViewMode = (
  forRide = true,
  viewMode: RideMemberViewMode
) => forRide && viewMode === 'COMPACT';

export const subscribeRideMemberViewMode = (onChange: () => void) => {
  if (typeof window === 'undefined') return () => {};

  const handleStorage = (event: StorageEvent) => {
    if (event.key === RIDE_MEMBER_VIEW_MODE_STORAGE_KEY) {
      onChange();
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(RIDE_MEMBER_VIEW_MODE_CHANGE_EVENT, onChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(RIDE_MEMBER_VIEW_MODE_CHANGE_EVENT, onChange);
  };
};

const {
  rides: {
    rideByIdData,
    standingOrderByIdData,
    isRideCopy,
    createSOByRide,
    savedRideId,
  },
  users: { userInfo },
} = store.getState();

const getBaseDate = (
  getValues: any,
  segments: SegmentType[],
  forRide?: boolean
) => {
  if (forRide === true) {
    return getValues('rideRequestDate') || segments?.[0]?.rideDate;
  }

  if (forRide === false) {
    return (
      getValues('startDate') ||
      getValues('rideRequestDate') ||
      segments?.[0]?.rideDate
    );
  }

  return (
    getValues('rideRequestDate') ||
    getValues('startDate') ||
    segments?.[0]?.rideDate
  );
};

const toMinutes = (time?: string | null): number | null => {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const resolveSegmentOrderingValue = (
  segment?: Pick<SegmentType, 'orderingValue'> | null,
  fallbackIndex = 0
) => {
  const orderingValue = Number(segment?.orderingValue);

  return Number.isFinite(orderingValue) && orderingValue > 0
    ? orderingValue
    : fallbackIndex + 1;
};

const hasSameOrderingGroup = (
  segments: Array<Pick<SegmentType, 'orderingValue'> | null> = [],
  firstIndex: number,
  secondIndex: number
) => {
  if (firstIndex < 0 || secondIndex < 0) return false;
  if (firstIndex >= segments.length || secondIndex >= segments.length)
    return false;

  return (
    resolveSegmentOrderingValue(segments[firstIndex], firstIndex) ===
    resolveSegmentOrderingValue(segments[secondIndex], secondIndex)
  );
};

const getNextDistinctOrderingIndex = (
  segments: Array<Pick<SegmentType, 'orderingValue'> | null> = [],
  index: number
) => {
  const currentOrderingValue = resolveSegmentOrderingValue(
    segments[index],
    index
  );

  for (let i = index + 1; i < segments.length; i += 1) {
    if (resolveSegmentOrderingValue(segments[i], i) !== currentOrderingValue) {
      return i;
    }
  }

  return null;
};

const getPreviousDistinctOrderingIndex = (
  segments: Array<Pick<SegmentType, 'orderingValue'> | null> = [],
  index: number
) => {
  const currentOrderingValue = resolveSegmentOrderingValue(
    segments[index],
    index
  );

  for (let i = index - 1; i >= 0; i -= 1) {
    if (resolveSegmentOrderingValue(segments[i], i) !== currentOrderingValue) {
      return i;
    }
  }

  return null;
};

export const setNextDayBoundary = (
  boundaryIndex: number | null,
  getValues: any,
  setValue: any,
  forRide?: boolean
) => {
  const segments: SegmentType[] = getValues('ridesSegments') || [];

  const baseDate = getBaseDate(getValues, segments, forRide);

  segments.forEach((_, idx) => {
    const isNextDay = boundaryIndex !== null ? idx >= boundaryIndex : false;
    setValue(`ridesSegments.${idx}.nextDay`, isNextDay, { shouldDirty: true });

    const nextDate =
      boundaryIndex !== null && idx >= boundaryIndex
        ? addOneDay(baseDate)
        : baseDate;

    setValue(`ridesSegments.${idx}.rideDate`, nextDate, {
      shouldDirty: true,
    });
  });
};

export const syncSegmentsDates = (
  getValues: any,
  setValue: any,
  forRide?: boolean
) => {
  const segments: SegmentType[] = getValues('ridesSegments') || [];
  const baseDate = getBaseDate(getValues, segments, forRide);

  if (!baseDate) return;

  segments.forEach((segment, idx) => {
    const nextDate = segment.nextDay ? addOneDay(baseDate) : baseDate;

    setValue(`ridesSegments.${idx}.rideDate`, nextDate, {
      shouldDirty: true,
    });
  });
};

export const syncNextDayBoundaryFromTimes = (
  getValues: any,
  setValue: any,
  forRide?: boolean,
  suppressAutoNextDay?: boolean
) => {
  const segments: SegmentType[] = getValues('ridesSegments') || [];
  const existingIndex = segments.findIndex((segment) => segment?.nextDay);

  const nextDayManuallyRemoved = getValues('nextDayManuallyRemoved');

  if (suppressAutoNextDay && nextDayManuallyRemoved) {
    return;
  }

  let hasUncertain = false;
  let boundaryIndex: number | null = null;

  for (let i = 0; i < segments.length; i += 1) {
    const isWillCall = segments[i]?.callIndicator === true;
    const isSameGroupAsPrev = hasSameOrderingGroup(segments, i, i - 1);
    const prevNextDay = i > 0 && !isSameGroupAsPrev && segments[i - 1]?.nextDay;

    if (isWillCall) {
      if (prevNextDay) {
        boundaryIndex = i;
        break;
      }
      continue;
    }

    const currPickup = toMinutes(segments[i]?.scheduledPickupTime);
    const currDrop = toMinutes(segments[i]?.scheduledAppointmentTime);

    const { loadDuration, unloadDuration } = getValues('modeOfTransport') || {
      loadDuration: '00:00',
      unloadDuration: '00:00',
    };

    if (i > 0 && !isSameGroupAsPrev) {
      const prevPickup = segments[i - 1]?.scheduledPickupTime;
      const prevDuration = segments[i - 1]?.estimatedTripDuration;
      const prevNextDay = segments[i - 1]?.nextDay;

      const { nextDay: rollsOver } = addOrSubtractDuration(
        prevPickup,
        [prevDuration, loadDuration, unloadDuration],
        true
      );

      if (!prevNextDay && rollsOver) {
        boundaryIndex = i;
        break;
      }
    }

    if (currPickup === null || currDrop === null) {
      hasUncertain = true;
      continue;
    }

    if (currDrop < currPickup) {
      setValue(`ridesSegments.${i}.nextDay`, false, { shouldDirty: true });
      boundaryIndex = getNextDistinctOrderingIndex(segments, i);

      if (boundaryIndex === null) {
        setNextDayBoundary(null, getValues, setValue, forRide);
        return;
      }
      break;
    }

    if (i > 0 && !isSameGroupAsPrev) {
      const prevDrop = toMinutes(segments[i - 1]?.scheduledAppointmentTime);
      const prevNextDay = segments[i - 1]?.nextDay;

      if (prevDrop === null || currPickup === null) {
        hasUncertain = true;
      } else if (!prevNextDay && currPickup < prevDrop) {
        boundaryIndex = i;
        break;
      }
    }
  }

  if (boundaryIndex !== null) {
    if (suppressAutoNextDay && nextDayManuallyRemoved) {
      return;
    }
    setNextDayBoundary(boundaryIndex, getValues, setValue, forRide);
    return;
  }

  if (existingIndex !== -1 && !suppressAutoNextDay) {
    setNextDayBoundary(existingIndex, getValues, setValue, forRide);
    return;
  }

  if (existingIndex === -1 && (suppressAutoNextDay || nextDayManuallyRemoved)) {
    return;
  }

  // TODO remove this case after proper check
  // if (!hasUncertain && existingIndex !== -1) {
  if (!hasUncertain && existingIndex === -1) {
    setNextDayBoundary(null, getValues, setValue, forRide);
  }
};

export const handleAddNextDayToSegments = (
  index: number,
  getValues: any,
  setValue: any,
  forRide: boolean
) => {
  setValue('nextDayManuallyRemoved', false, { shouldDirty: false });
  setNextDayBoundary(index, getValues, setValue, forRide);
};

export const handleSubtractNextDayFromSegments = (
  _index: number,
  getValues: any,
  setValue: any,
  forRide: boolean
) => {
  setNextDayBoundary(null, getValues, setValue, forRide);
};

export const handleMinTime = (
  nextDay: boolean,
  field = '00:00',
  duration = '00:00',
  loadDuration = '00:00',
  unloadDuration = '00:00'
): string => {
  if (nextDay) return '00:00';

  return addOrSubtractDuration(
    field,
    [duration, loadDuration, unloadDuration],
    true
  ).time;
};

export const parseTimeStringToDate = (time?: string) => {
  if (!time) return;

  const normalized = time.replace(/[։]/g, ':');
  const [hours, minutes] = normalized.split(':').map(Number);

  return new Date(0, 0, 0, hours, minutes);
};

export const checkCorrectTime = ({
  index,
  value,
  getValues,
  setValue,
  setError,
  clearErrors,
  isCopy = false,
  processedIndexes = new Set<number>(),
}) => {
  if (processedIndexes.has(index)) return;
  processedIndexes.add(index);

  const segments = getValues('ridesSegments') || [];
  const currentValue = parseTimeStringToDate(value);
  const dropTimeMinutes = toMinutes(
    getValues(`ridesSegments.${index}.scheduledAppointmentTime`)
  );
  const pickupTimeMinutes = toMinutes(
    getValues(`ridesSegments.${index}.scheduledPickupTime`)
  );

  const isRollOverInSameSegment =
    dropTimeMinutes !== null &&
    pickupTimeMinutes !== null &&
    dropTimeMinutes < pickupTimeMinutes;

  const prevSegmentIndex = getPreviousDistinctOrderingIndex(segments, index);
  if (prevSegmentIndex !== null) {
    const prevDropOffTimeStr = getValues(
      `ridesSegments.${prevSegmentIndex}.scheduledAppointmentTime`
    );
    const prevDropOffTime = parseTimeStringToDate(prevDropOffTimeStr);

    const isPrevNextDay = !!getValues(
      `ridesSegments.${prevSegmentIndex}.nextDay`
    );
    const isCurrentNextDay = !!getValues(`ridesSegments.${index}.nextDay`);
    const isTransitionFromPrevToNextDayActual =
      !isPrevNextDay && isCurrentNextDay;

    if (
      !isTransitionFromPrevToNextDayActual &&
      !isRollOverInSameSegment &&
      currentValue &&
      prevDropOffTime &&
      currentValue < prevDropOffTime
    ) {
      const message = `The segment ${index + 1} Pickup Time cannot be earlier than the ${prevSegmentIndex + 1} segment Drop-Off Time.`;
      const currentError = getValues(
        `ridesSegments.${index}.scheduledPickupTime_error`
      );
      if (isCopy) {
        toast.error(`Copy validation: ${message}`);
      } else if (currentError !== message) {
        toast.error(message);
        setValue(`ridesSegments.${index}.scheduledPickupTime_error`, message);
      }
      setError(
        `ridesSegments.${index}.scheduledPickupTime`,
        { type: 'manual', message },
        { shouldFocus: true }
      );
    } else {
      clearErrors(`ridesSegments.${index}.scheduledPickupTime`);
      setValue(`ridesSegments.${index}.scheduledPickupTime_error`, null);
    }
  } else {
    clearErrors(`ridesSegments.${index}.scheduledPickupTime`);
    setValue(`ridesSegments.${index}.scheduledPickupTime_error`, null);
  }

  const nextSegmentIndex = getNextDistinctOrderingIndex(segments, index);
  if (nextSegmentIndex !== null) {
    const nextPickupTimeStr = getValues(
      `ridesSegments.${nextSegmentIndex}.scheduledPickupTime`
    );
    const nextPickupTime = parseTimeStringToDate(nextPickupTimeStr);

    const isCurrentNextDay = !!getValues(`ridesSegments.${index}.nextDay`);
    const isNextSegmentNextDay = !!getValues(
      `ridesSegments.${nextSegmentIndex}.nextDay`
    );
    const isTransitionToNextDay = !isCurrentNextDay && isNextSegmentNextDay;

    const currentDropOffTime = parseTimeStringToDate(
      getValues(`ridesSegments.${index}.scheduledAppointmentTime`)
    );

    if (
      !isTransitionToNextDay &&
      nextPickupTime &&
      currentDropOffTime &&
      nextPickupTime < currentDropOffTime
    ) {
      const message = `The segment ${index + 1} Pickup Time cannot be earlier than the ${nextSegmentIndex + 1} segment Drop-Off Time.`;
      const nextError = getValues(
        `ridesSegments.${nextSegmentIndex}.scheduledPickupTime_error`
      );
      if (!isCopy && nextError !== message) {
        toast.error(message);
        setValue(
          `ridesSegments.${nextSegmentIndex}.scheduledPickupTime_error`,
          message
        );
      }
      setError(
        `ridesSegments.${nextSegmentIndex}.scheduledPickupTime`,
        { type: 'manual', message },
        { shouldFocus: true }
      );
    } else {
    }

    const nextSegmentDropOffTime = getValues(
      `ridesSegments.${nextSegmentIndex}.scheduledAppointmentTime`
    );
    if (nextSegmentDropOffTime) {
      checkCorrectTime({
        index: nextSegmentIndex,
        value: nextSegmentDropOffTime,
        getValues,
        setValue,
        setError,
        clearErrors,
        isCopy,
        processedIndexes,
      });
    }
  }
};

export const subtractFromDepart = ({
  value = '00:00',
  index,
  getValues,
  setValue,
  forRide,
  isDisabledNextDayButton,
  setError,
  clearErrors,
  setIsDropOffCheckedFirst,
  suppressAutoNextDay = false,
}) => {
  const { loadDuration, unloadDuration } = getValues('modeOfTransport') || {
    loadDuration: '00:00',
    unloadDuration: '00:00',
  };
  const prevSegmentStatusId = getValues(`ridesSegments.${index - 1}`)?.status
    ?.id;

  setIsDropOffCheckedFirst &&
    setIsDropOffCheckedFirst(
      !getValues(`ridesSegments.${index}.scheduledPickupTime`)
    );

  const { nextDay, time } = addOrSubtractDuration(
    value,
    [
      getValues(`ridesSegments.${index}.estimatedTripDuration`),
      loadDuration,
      unloadDuration,
    ],
    false
  );

  const convertedTime = convertTimeBetweenLocations(
    time,
    getValues(`ridesSegments.${index}`)?.dropOffLocation,
    getValues(`ridesSegments.${index}`)?.pickupLocations
  );

  setValue(`ridesSegments.${index}.scheduledPickupTime`, convertedTime);
  clearErrors(`ridesSegments.${index}.scheduledAppointmentTime`);
  syncNextDayBoundaryFromTimes(
    getValues,
    setValue,
    forRide,
    suppressAutoNextDay
  );

  if (!isSegmentCompleted(prevSegmentStatusId)) {
    checkCorrectTime({
      index,
      value,
      getValues,
      setValue,
      setError,
      clearErrors,
    });
  }
};

export const addToPickup = ({
  value = '00:00',
  index,
  getValues,
  setValue,
  forRide,
  isDisabledNextDayButton,
  setError,
  clearErrors,
  suppressAutoNextDay = false,
}: {
  value: string;
  index: number;
  getValues: any;
  setValue: any;
  forRide: boolean;
  isDisabledNextDayButton: boolean;
  setError: any;
  clearErrors: any;
  suppressAutoNextDay?: boolean;
}) => {
  const { loadDuration, unloadDuration } = getValues('modeOfTransport') || {
    loadDuration: '00:00',
    unloadDuration: '00:00',
  };
  const segmentStatusId = getValues(`ridesSegments.${index}`)?.status?.id;

  const { nextDay, time } = addOrSubtractDuration(
    value,
    [
      getValues(`ridesSegments.${index}.estimatedTripDuration`),
      loadDuration,
      unloadDuration,
    ],
    true
  );

  const convertedTime = convertTimeBetweenLocations(
    time,
    getValues(`ridesSegments.${index}`)?.pickupLocations,
    getValues(`ridesSegments.${index}`)?.dropOffLocation
  );

  if (nextDay) {
    setValue(`ridesSegments.${index}.scheduledAppointmentTime`, convertedTime);
    clearErrors(`ridesSegments.${index}.scheduledAppointmentTime`);
  } else {
    setValue(`ridesSegments.${index}.scheduledAppointmentTime`, convertedTime);
    clearErrors(`ridesSegments.${index}.scheduledAppointmentTime`);
  }

  if (!isSegmentCompleted(segmentStatusId)) {
    checkCorrectTime({
      index,
      value: convertedTime,
      getValues,
      setValue,
      setError,
      clearErrors,
    });
  }

  syncNextDayBoundaryFromTimes(
    getValues,
    setValue,
    forRide,
    suppressAutoNextDay
  );
};

export const validateAllSegmentsTime = ({
  getValues,
  setValue,
  setError,
  clearErrors,
}: {
  getValues: any;
  setValue: any;
  setError: any;
  clearErrors: any;
}) => {
  const segments = getValues('ridesSegments') || [];
  const processedIndexes = new Set<number>();
  segments.forEach((segment: any, index: number) => {
    const value = segment.scheduledAppointmentTime;
    if (value) {
      checkCorrectTime({
        index,
        value,
        getValues,
        setValue,
        setError,
        clearErrors,
        isCopy: true,
        processedIndexes,
      });
    }
  });
};

// TODO remove after proper check
// export const getMinPickUpTime = (getValues: any, index: number) => {
//   const toMinutes = (time: string = '00:00') => {
//     const [h, m] = time?.split(':').map(Number);
//     return h * 60 + m;
//   };
//
//   const segments: SegmentType[] = getValues('ridesSegments') || [];
//   const hasPreviousNextDay = segments
//     .slice(0, index + 1)
//     .some((segment) => segment?.nextDay);
//
//   const pickupLoc = getValues(`ridesSegments.${index}`)?.pickupLocations;
//   const lat = pickupLoc?.latitude;
//   const lng = pickupLoc?.longitude;
//
//   let tz: string | null = null;
//   if (
//     pickupLoc &&
//     typeof lat === 'number' &&
//     typeof lng === 'number' &&
//     !Number.isNaN(lat) &&
//     !Number.isNaN(lng)
//   ) {
//     try {
//       tz = tzlookup(lat, lng);
//     } catch (e) {
//       tz = null;
//     }
//   }
//
//   const nowTz = tz ? moment().tz(tz) : moment();
//   const currentDateStr = tz
//     ? nowTz.format('MM/DD/YYYY')
//     : format(Date.now(), 'MM/dd/yyyy');
//   const rideDateStr: string | undefined = getValues('rideRequestDate');
//   const isTodayInPickupTz = !!rideDateStr && rideDateStr === currentDateStr;
//
//   const now = addOrSubtractDuration(
//     convertTimeByCoords(new Date(), pickupLoc),
//     ['00:00'],
//     true
//   ).time;
//
//   const prevSegmentStatusId = getValues(`ridesSegments.${index - 1}`)?.status
//     ?.id;
//
//   const prevDepartTime =
//     getValues(`ridesSegments.${index - 1}`)?.scheduledAppointmentTime ||
//     '00:00';
//   const prevPickupTime =
//     getValues(`ridesSegments.${index - 1}`)?.scheduledPickupTime || '00:00';
//
//   const maxTime =
//     toMinutes(now) > toMinutes(prevPickupTime) ? now : prevPickupTime;
//
//   return handleMinTime(
//     hasPreviousNextDay,
//     index === 0
//       ? isTodayInPickupTz
//         ? now
//         : '00:00'
//       : isSegmentCompleted(prevSegmentStatusId)
//         ? maxTime
//         : prevDepartTime
//   );
// };

export const getMinPickUpTime = ({
  getValues,
  index,
  isRideShare = true,
}: {
  getValues: any;
  index: number;
  isRideShare: boolean;
}) => {
  const completedStatuses = [
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    18,
    19,
    20,
    25,
    26,
    27,
    28,
    ...(!isRideShare ? [1, 9999] : []),
  ];

  const toMinutes = (time: string = '00:00') => {
    const [h, m] = time?.split(':').map(Number);
    return h * 60 + m;
  };

  const allSegments: any[] = getValues('ridesSegments') || [];
  const currentSegment = allSegments[index];

  const filteredSegments = allSegments.filter(
    (segment) => !completedStatuses.includes(segment?.status?.id)
  );

  const filteredIndex = filteredSegments.findIndex((s) => s === currentSegment);

  if (filteredIndex === -1) return '00:00';

  const hasPreviousNextDay = filteredSegments
    .slice(0, filteredIndex + 1)
    .some((segment) => segment?.nextDay);
  const CONTRACTOR = whichContractor(currentSegment.contractor);

  const pickupLoc = currentSegment?.pickupLocations;
  const lat = pickupLoc?.latitude;
  const lng = pickupLoc?.longitude;

  let tz: string | null = null;
  if (
    pickupLoc &&
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng)
  ) {
    try {
      tz = tzlookup(lat, lng);
    } catch (e) {
      tz = null;
    }
  }

  const nowTz = tz ? moment().tz(tz) : moment();
  const currentDateStr = tz
    ? nowTz.format('MM/DD/YYYY')
    : format(Date.now(), 'MM/dd/yyyy');

  const rideDateStr: string | undefined = getValues('rideRequestDate');
  const isTodayInPickupTz = !!rideDateStr && rideDateStr === currentDateStr;

  const now = addOrSubtractDuration(
    convertTimeByCoords(new Date(), pickupLoc),
    ['00:00'],
    true
  ).time;

  const prevDistinctIndex = getPreviousDistinctOrderingIndex(
    filteredSegments,
    filteredIndex
  );
  const prevSegment =
    prevDistinctIndex !== null
      ? filteredSegments[prevDistinctIndex]
      : undefined;
  const prevSegmentStatusId = prevSegment?.status?.id;

  const prevDepartTime = prevSegment?.scheduledAppointmentTime || '00:00';
  const prevPickupTime = prevSegment?.scheduledPickupTime || '00:00';

  const maxTime =
    toMinutes(now) > toMinutes(prevPickupTime) ? now : prevPickupTime;

  return handleMinTime(
    hasPreviousNextDay,
    prevDistinctIndex === null
      ? isTodayInPickupTz
        ? now
        : '00:00'
      : isSegmentCompleted(prevSegmentStatusId, CONTRACTOR !== 'other')
        ? maxTime
        : prevDepartTime
  );
};

const resetSegmentToDefault = (segment: SegmentType) => {
  const { id, reschedule, ...withoutIdSegment } = segment;
  return {
    id: null,
    ...withoutIdSegment,
    scheduledPickupTime: withoutIdSegment.callIndicator
      ? null
      : withoutIdSegment.scheduledPickupTime,
    scheduledAppointmentTime: withoutIdSegment.callIndicator
      ? null
      : withoutIdSegment.scheduledAppointmentTime,
    rideId: null,
    rideRemoteId: null,
    rideType: '',
    rideBody: '',
    priceChangedToken: '',
    rideDate: '',
    status: {
      id: 100,
      label: 'New',
      value: 'New',
    },
    canCancel: null,
    canceledBy: null,
    cancellationReason: null,
    rideSupportUrl: null,
    vehicle: {},
    driver: {},
  };
};

const getCopyableRideSegments = (segments: SegmentType[] = []) => {
  const seenOrderingValues = new Set<number>();

  return segments.filter((segment, index) => {
    const orderingValue = getSegmentOrderingValue(segment, index);

    if (seenOrderingValues.has(orderingValue)) {
      return false;
    }

    seenOrderingValues.add(orderingValue);
    return true;
  });
};

export const copyRideToDefault = (
  ride: RideItemType,
  isItStandingOrder?: boolean
) => {
  const { id, ...withoutIdRide } = ride;
  const copyableSegments = getCopyableRideSegments(
    withoutIdRide.ridesSegments as SegmentType[]
  );

  if (isItStandingOrder) {
    return {
      ...withoutIdRide,
      rideOrderConfirmationCode: '',
      startDate: '',
      endDate: '',
      scheduledDays: [],
      ridesSegments: copyableSegments.map((segment: SegmentType) =>
        resetSegmentToDefault(segment)
      ),
    };
  }

  return {
    ...withoutIdRide,
    rideOrderConfirmationCode: '',
    rideRequestDate: '',
    standingOrder: null,
    ridesSegments: copyableSegments.map((segment: SegmentType) =>
      resetSegmentToDefault(segment)
    ),
  };
};

export const convertTimeByCoords = (
  input: Date,
  location?: locationType
): string => {
  try {
    if (
      !location ||
      typeof location.latitude !== 'number' ||
      typeof location.longitude !== 'number' ||
      isNaN(location.latitude) ||
      isNaN(location.longitude) ||
      location.latitude < -90 ||
      location.latitude > 90 ||
      location.longitude < -180 ||
      location.longitude > 180
    ) {
      return moment(input).format('HH:mm');
    }

    const tz = tzlookup(location.latitude, location.longitude);
    return moment.utc(input).tz(tz).format('HH:mm');
  } catch (err) {
    logger.error('convertTimeByCoords error:', err);

    return moment(input).format('HH:mm');
  }
};

const extractMinutes = ({ time }: { time: string }) => {
  const [currentHours, currentMinutes] = time
    .split(':')
    .map((val) => parseInt(val, 10));
  return currentHours * 60 + currentMinutes;
};

export const correctTimeSetter = ({
  openedTime,
  savedTime,
}: {
  openedTime: string;
  savedTime: string;
}): string => {
  const openedMinute = extractMinutes({ time: openedTime });
  const savedMinute = extractMinutes({ time: savedTime });
  const pastMinutes = savedMinute - openedMinute;
  if (pastMinutes > 5) {
    toast.error(toastMessage.RIDE.activationTimePassed);
    return openedTime;
  } else if (pastMinutes < 0) {
    return openedTime;
  } else {
    return savedTime;
  }
};

export const convertTimeBetweenLocations = (
  time: string,
  fromLocation: locationType,
  toLocation: locationType
): string => {
  if (
    !fromLocation?.latitude ||
    !fromLocation?.longitude ||
    !toLocation?.latitude ||
    !toLocation?.longitude
  ) {
    return time;
  }
  const fromTz = tzlookup(fromLocation.latitude, fromLocation.longitude);
  const toTz = tzlookup(toLocation.latitude, toLocation.longitude);

  const today = moment().format('YYYY-MM-DD');

  const fromMoment = moment.tz(`${today} ${time}`, 'YYYY-MM-DD HH:mm', fromTz);

  if (!fromMoment.isValid()) {
    logger.warn('Invalid time:', time);
    return time;
  }

  return fromMoment.clone().tz(toTz).format('HH:mm');
};

export const getSegmentStatusPayload = (segmentId?: unknown) =>
  segmentId ? {} : { status: null };

export const isSegmentCompleted = (statusId: number, isRideShare = true) => {
  const completeStatusesIds = [
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    28,
    ...(!isRideShare ? [1, 9999] : []),
  ];
  return completeStatusesIds.includes(statusId);
};

export const rideOrStandingOrder = {
  ride: {
    title: 'Ride',
    forRide: true,
    startDate: 'rideRequestDate' as const,
    endDate: 'rideRequestDate' as const,
    data: rideByIdData,
    rideExistsMessage: 'The member already has a ride for this DOS.',
    defaultSelected: {
      id: 26,
      value: '26',
      label: 'Ride modified',
    },
    url: '/rides/information/',
    dispatch: () => {
      store.dispatch(setIsRideSaved(true));
      store.dispatch(setIsRideCopy(false));
    },
    isCopy: isRideCopy,
  },
  standingOrder: {
    title: 'Standing order',
    startDate: 'startDate' as const,
    endDate: 'endDate' as const,
    data: standingOrderByIdData,
    rideExistsMessage: `The member already has a ride for this DOS. The ride
                      you're attempting to save has times that overlap with the
                      existing ride below.`,
    forRide: false,
    defaultSelected: {
      id: 33,
      value: '33',
      label: 'Standing order modified',
    },
    url: '/standing-order/details/',
    dispatch: () => {
      store.dispatch(setIsSOSaved(true));
      store.dispatch(setCreateSOByRide(false));
    },
    isCopy: createSOByRide,
  },
};

export const preventRideCreation = () => {
  AlertModal.show({
    title: 'Validation Error',
    message: 'Your limitation exceeded. Please contact your administrator.',
    buttons: [],
  });
};

export const addOneDayInSegment = (dateStr: string) => {
  const d = new Date(dateStr);
  const next = addDays(d, 1);
  return format(next, 'MM/dd/yyyy');
};

export const statusState = ({
  statusId,
  stepName,
}: {
  statusId: number;
  stepName: 'sent' | 'scheduled';
}): StepStatus => {
  const meta = allStatuses?.[statusId];
  const loaderStatus: StepStatus = meta?.loaderStatus ?? 'pending';

  if (loaderStatus === 'error') {
    if (stepName === 'scheduled') return 'error';
    return 'success';
  }

  const isSuccess = loaderStatus === 'success';
  const isSentEvent = statusId === RIDE_STATUS_IDS.SENT_TO_RIDESHARE;
  const isScheduledEvent = statusId === RIDE_STATUS_IDS.SCHEDULED;

  const isSent = isSentEvent || isSuccess;
  const isScheduled = isScheduledEvent || (isSuccess && !isSentEvent);

  if (stepName === 'sent') return isSent ? 'success' : 'pending';
  return isScheduled ? 'success' : 'pending';
};

export const rideModalFlow = ({
  isNew,
  statusId,
  title,
}: {
  isNew: boolean;
  statusId: number;
  title: string;
}) => ({
  title,
  steps: [
    {
      id: 'saved',
      label: 'Ride segment saved to database',
      status: 'success' as StepStatus,
    },
    {
      id: 'sent',
      label: 'Ride segment sent to rideshare via API',
      status: isNew
        ? ('pending' as StepStatus)
        : statusState({ statusId, stepName: 'sent' }),
    },
    {
      id: 'scheduled',
      label: 'Ride segment scheduled successfully',
      status: isNew
        ? ('pending' as StepStatus)
        : statusState({ statusId, stepName: 'scheduled' }),
    },
  ],
});

/*** Loader logic ***/

const IMPACT_FIELDS = new Set([
  'callIndicator',
  'rideDate',
  'scheduledPickupTime',
  'scheduledAppointmentTime',
  'pickupLocations',
  'dropOffLocation',
  'contractor',
]);

const NON_RIDESHARE_RIDE_LEVEL_FIELDS = new Set([
  'authorizedNumber',
  'extraPassenger',
  'ridesTags',
  'schedulerAuthorizedContact',
  'relation',
  'callBackNumber',
  'primaryType',
]);

const getAllIds = (ridesSegments: any) =>
  ridesSegments.map((s) => s?.id).filter(Boolean) as number[];

const hasRideshareImpactingRideLevelDirty = (df: any) =>
  Object.keys(df || {}).some(
    (k) => k !== 'ridesSegments' && !NON_RIDESHARE_RIDE_LEVEL_FIELDS.has(k)
  );

const getDirtyIndexes = (df: any) =>
  (df?.ridesSegments || [])
    .map((segObj: any, idx: number) => (segObj ? idx : -1))
    .filter((idx: number) => idx !== -1);

const getIdByIndex = (idx: number, ridesSegments: any) =>
  ridesSegments?.[idx]?.id as number | undefined;

export const isOnlyAuthorizedNumberDirty = (dirtyFields: any) => {
  const dirtyKeys = Object.keys(dirtyFields || {}).filter(
    (key) => key !== 'ridesSegments'
  );

  return (
    dirtyKeys.length > 0 &&
    dirtyKeys.every((key) => key === 'authorizedNumber') &&
    !getDirtyIndexes(dirtyFields).length
  );
};

export const collectSegmentIds = ({
  ridesSegments,
  mode,
  dirtySnapshot,
  dirtyFields,
}: {
  mode: 'changed' | 'impacted';
  dirtySnapshot?: any;
  ridesSegments: any;
  dirtyFields: any;
}) => {
  const df: any = dirtySnapshot ?? dirtyFields;
  const ids = new Set<number>();

  if (hasRideshareImpactingRideLevelDirty(df))
    getAllIds(ridesSegments).forEach((id) => ids.add(id));

  const segDirty = df?.ridesSegments || [];

  getDirtyIndexes(df).forEach((idx) => {
    if (mode === 'impacted') {
      const isImpacted = Object.keys(segDirty[idx] || {}).some((k) =>
        IMPACT_FIELDS.has(k)
      );
      if (!isImpacted) return;
    }

    const id = getIdByIndex(idx, ridesSegments);
    if (id) ids.add(id);
  });

  return [...ids];
};

export const getSegmentOrderingValue = (
  segment?: Pick<SegmentType, 'orderingValue'> | null,
  fallbackIndex = 0
) => resolveSegmentOrderingValue(segment, fallbackIndex);

export const getAlphabeticalSequence = (sequenceIndex: number) => {
  let currentIndex = sequenceIndex;
  let result = '';

  do {
    result = String.fromCharCode(97 + (currentIndex % 26)) + result;
    currentIndex = Math.floor(currentIndex / 26) - 1;
  } while (currentIndex >= 0);

  return result;
};

export const updateAllSegmentLettering = (
  ridesSegments: Array<Pick<
    SegmentType,
    'orderingValue' | 'reschedule'
  > | null> = []
) => {
  const groups: Record<number, number[]> = {};

  ridesSegments.forEach((segment, index) => {
    const baseValue = getSegmentOrderingValue(segment, index);
    if (!groups[baseValue]) groups[baseValue] = [];
    groups[baseValue].push(index);
  });

  const results: Array<{
    index: number;
    suffix: string | null;
    baseValue: number;
  }> = [];

  Object.entries(groups).forEach(([baseValueStr, indices]) => {
    const baseValue = Number(baseValueStr);

    const hasReschedule = indices.some((idx) => ridesSegments[idx]?.reschedule);

    const needsLettering = indices.length > 1 || hasReschedule;

    indices.forEach((segmentIndex, sequenceIndex) => {
      results.push({
        index: segmentIndex,
        suffix: needsLettering ? getAlphabeticalSequence(sequenceIndex) : null,
        baseValue,
      });
    });
  });

  return results.sort((a, b) => a.index - b.index);
};

export const getRideSegmentDisplayLabels = ({
  ridesSegments = [],
  index,
}: {
  ridesSegments?: Array<Pick<SegmentType, 'orderingValue'> | null>;
  index: number;
}) => {
  const currentSegment = ridesSegments[index];
  const baseOrderingValue = getSegmentOrderingValue(currentSegment, index);

  const groupedIndexes = ridesSegments.reduce<number[]>(
    (acc, segment, segmentIndex) => {
      if (
        getSegmentOrderingValue(segment, segmentIndex) === baseOrderingValue
      ) {
        acc.push(segmentIndex);
      }

      return acc;
    },
    []
  );

  if (groupedIndexes.length <= 1) {
    return {
      pickupLabel: String(baseOrderingValue),
      dropOffLabel: String(baseOrderingValue + 1),
    };
  }

  const sequenceIndex = Math.max(0, groupedIndexes.indexOf(index));
  const sequenceSuffix = getAlphabeticalSequence(sequenceIndex);

  return {
    pickupLabel: `${baseOrderingValue}${sequenceSuffix}`,
    dropOffLabel: `${baseOrderingValue + 1}${sequenceSuffix}`,
  };
};

export const isRescheduledRideSegment = ({
  ridesSegments = [],
  index,
}: {
  ridesSegments?: Array<Pick<
    SegmentType,
    'orderingValue' | 'reschedule'
  > | null>;
  index: number;
}) => {
  const currentSegment = ridesSegments[index];

  if (!currentSegment) return false;
  if (Boolean(currentSegment.reschedule)) return true;

  const baseOrderingValue = getSegmentOrderingValue(currentSegment, index);

  const groupedIndexes = ridesSegments.reduce<number[]>(
    (acc, segment, segmentIndex) => {
      if (
        getSegmentOrderingValue(segment, segmentIndex) === baseOrderingValue
      ) {
        acc.push(segmentIndex);
      }

      return acc;
    },
    []
  );

  if (groupedIndexes.length <= 1) return false;

  return groupedIndexes.indexOf(index) > 0;
};

export const orderingValueOptions = ({
  data = [],
  columnData,
}: {
  data: any;
  columnData: any;
}) => {
  const uniqueValues = Array.from(
    new Set(data.map((s: any) => s.orderingValue).filter((v: any) => v != null))
  ).sort((a: any, b: any) => a - b);

  const orderingValueOptions = uniqueValues.map((_val: any, index: number) => ({
    id: index + 1,
    value: String(index + 1),
    label: String(index + 1),
  }));

  const columns = (columnData.columns || []).map((col: any) =>
    col.filterKey === 'orderingValue'
      ? { ...col, filterOptions: orderingValueOptions }
      : col
  );

  return { columns };
};

const rideSegmentTemplate = ({
  data,
  estimatedMiles,
  estimatedDuration,
  rideId,
  forRide,
}: {
  data: any;
  estimatedMiles: any[];
  estimatedDuration: any[];
  rideId: number;
  forRide: boolean;
}) => {
  return data.ridesSegments.map((segment, index, array) => {
    const {
      pickupLocations,
      dropOffLocation,
      estimatedTripDuration,
      contractor,
      authorization,
      status,
      ridesSegmentTimes,
      id: segmentID,
      rideBody,
      rideType,
      pickUpContactPhoneInvalid,
      dropOffContactPhoneInvalid,
      ...rest
    } = segment;

    const estMiles = !!rest.estMiles ? parseFloat(rest.estMiles) : 0;
    estimatedMiles.push(estMiles);
    estimatedDuration.push(estimatedTripDuration);

    const isLastElement = index === array.length - 1;
    const pickupLocationsId = pickupLocations?.id as number;
    const dropOffLocationId = dropOffLocation?.id as number;

    return {
      ...rest,
      orderingValue: segment.orderingValue,
      ...getSegmentStatusPayload(segmentID),
      ...(segment.reschedule === true
        ? { reschedule: true }
        : { reschedule: false }),
      estMiles,
      dropOffContactPhone: segment?.dropOffContactPhone,
      estimatedTripDuration: addSeconds(estimatedTripDuration) || '00:00',
      rideId,
      dropOffLocationId,
      pickupLocationsId,
      ...(segmentID ? { id: segmentID } : {}),
      ...(!isLastElement
        ? { estDropMiles: parseFloat(array[index + 1].estMiles) }
        : {}),
      ...(!isLastElement
        ? {
            estimatedDropTripDuration: addSeconds(
              array[index + 1].estimatedTripDuration
            ),
          }
        : {}),
      // ...(await generateRideBody(index)),
      ...(forRide ? { rideBody: JSON.stringify(rideBody) } : {}),
      ...(forRide ? { rideType } : {}),
      authorizationId: segment.authorization?.id,
      contractorId: contractor?.id,
      pickUpContactPhoneType: segment?.pickUpContactPhoneType,
      dropOffContactPhoneType: segment?.dropOffContactPhoneType,
    };
  });
};

const rideSpecificConfig = (data: any) => {
  return {
    rideRequestDate: data?.rideRequestDate,
  };
};

const standingOrderSpecificConfig = (data: any) => {
  return {
    rideId:
      createSOByRide && !isNaN(Number(savedRideId))
        ? Number(savedRideId)
        : null,
    endDate: data.endDate,
    startDate: data.startDate,
    scheduledDays: data?.scheduledDays?.map((el) => el?.id) ?? [],
  };
};

export const createBodyTemplate = ({
  data,
  memberId,
  rideId,
  forRide,
}: {
  data: any;
  memberId: number;
  rideId: number;
  forRide: boolean;
}) => {
  let estimatedMiles: any = [];
  let estimatedDuration: any = [];
  return {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    authorizedNumber: data.authorizedNumber,
    callBackNumber: data.callBackNumber,
    modeOfTransport: data.modeOfTransport.id,
    lobId: data.modeOfTransport.lobId,
    extraPassenger: data.extraPassenger.id,
    memberId,
    payorId: userInfo?.associated,
    schedulerAuthorizedContact: data.schedulerAuthorizedContact,
    totalDuration: formatTime(sumTimes(estimatedDuration), false),
    totalMile: parseFloat(estimatedMiles.reduce((a, b) => a + b, 0).toFixed(1)),
    ridesTags: data?.ridesTags ? data?.ridesTags.map((r) => r.id) : [],
    ridesSegments: [
      ...rideSegmentTemplate({
        data,
        estimatedMiles,
        estimatedDuration,
        rideId,
        forRide,
      }),
    ],
    relation: data.relation?.id,
    ...(!forRide
      ? standingOrderSpecificConfig(data)
      : rideSpecificConfig(data)),
  };
};

export const rideInfoTitleTemplate = (ride: {
  callIndicator: boolean;
  rideOrderConfirmationCode: string | null;
  rideDate: string | null;
  pickupTime: string | null;
}) => {
  const exactPickupTime =
    ride.callIndicator && !ride.pickupTime ? 'WCALL' : ride.pickupTime;
  return `${ride.rideOrderConfirmationCode} - ${ride.rideDate} - ${exactPickupTime}`;
};

export const hasInvalidSegmentPhone = (segment?: {
  pickUpContactPhoneInvalid?: boolean;
  dropOffContactPhoneInvalid?: boolean;
}) =>
  Boolean(
    segment?.pickUpContactPhoneInvalid || segment?.dropOffContactPhoneInvalid
  );
